import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";

/**
 * Modal popup shown when the rep re-runs deep research from the contact
 * side panel. Lists available seller profiles, lets the rep pick one
 * (defaulting to the currently active profile), and fires the run
 * callback. Keeps the run gesture explicit so the rep is always aware
 * which seller context the new research uses.
 */
const labelFor = (p) =>
  p?.name || p?.company_name || p?.product_name || "Unnamed profile";

const RerunResearchPopup = observer(
  ({
    profiles = [],
    initialSelectedId = "",
    onRun,
    onClose,
    onAddNew = () => {}
  }) => {
    const [selectedId, setSelectedId] = useState(initialSelectedId);

    // Default to the active profile (or first available) when popup opens.
    useEffect(() => {
      if (!selectedId && profiles.length > 0) {
        setSelectedId(initialSelectedId || profiles[0].id);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profiles.length]);

    // Esc closes
    useEffect(() => {
      const onKey = (e) => {
        if (e.key === "Escape") onClose();
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const handleRun = () => {
      if (!selectedId) return;
      onRun(selectedId);
    };

    return (
      <div className="drRerunOverlay" onClick={onClose}>
        <div className="drRerunCard" onClick={(e) => e.stopPropagation()}>
          <div className="drRerunHeader">
            <h3>Re-run deep research</h3>
            <span className="drRerunClose" onClick={onClose}>
              ✕
            </span>
          </div>

          <div className="drRerunBody">
            <p className="drRerunHint">
              Pick the seller profile to use for this run. The AI tailors
              conversation starters and selling signals to what you sell.
            </p>

            {profiles.length === 0 ? (
              <div className="drRerunEmpty">
                <p>No seller profiles yet — create one to research with.</p>
                <button
                  type="button"
                  className="drRerunAddBtn"
                  onClick={onAddNew}
                >
                  + Create seller profile
                </button>
              </div>
            ) : (
              <>
                <div className="drRerunList">
                  {profiles.map((p) => {
                    const checked = p.id === selectedId;
                    return (
                      <label
                        key={p.id}
                        className={`drRerunItem ${checked ? "checked" : ""}`}
                      >
                        <input
                          type="radio"
                          name="drRerunSeller"
                          checked={checked}
                          onChange={() => setSelectedId(p.id)}
                        />
                        <span className="drRerunRadio" />
                        <span className="drRerunItemLabel">{labelFor(p)}</span>
                      </label>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="drRerunAddLink"
                  onClick={onAddNew}
                >
                  + Add new seller profile
                </button>
              </>
            )}
          </div>

          <div className="drRerunFooter">
            <button
              type="button"
              className="drRerunBtn secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="drRerunBtn primary"
              onClick={handleRun}
              disabled={!selectedId}
            >
              Run research
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default RerunResearchPopup;
